import MomentUtils from "@date-io/moment";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import ClearIcon from "@material-ui/icons/Clear";
import {
  DatePicker,
  MuiPickersUtilsProvider,
  TimePicker,
} from "@material-ui/pickers";
import moment from "moment";
import momentTimezone from "moment-timezone";
import { useSnackbar } from "notistack";
import React from "react";
import { useSelector, useDispatch } from "react-redux";

import PropertiesSearchDialog from "../PropertiesSearch";
import UsersSearchDialog from "../UsersSearch";
import LoadingButton from "../../Buttons/Loading";
import ConditionalTooltip from "../../ConditionalTooltip";
import Divider from "../../Dividers/Dialog";
import Header from "../../Typography/Header";
import * as constants from "../../../constants";
import useCalendarBounds from "../../../hooks/useCalendarBounds";
import * as appsyncOperations from "../../../utils/appsyncOperations";
import getAllDivisionUsersById from "../../../utils/getAllDivisionUsersById";
import getFormattedShiftStatus from "../../../utils/getFormattedShiftStatus";
import getUserLevelAvatar from "../../../utils/getUserLevelAvatar";
import { isUserRole } from "../../../utils/user";
import PostOrders from "../../Lists/PostOrders";
import { init } from "../../../store/features/postOrders";

const ShiftDetailsDialog = ({
  open,
  onClose,
  onDelete,
  onSave,
  onPublish,
  onCoverageApproved,
  onCoverageDenied,
  shift,
}) => {
  const [propertyId, setPropertyId] = React.useState(null);
  const [userId, setUserId] = React.useState(null);
  const nowMoment = moment();
  const [startMoment, setStartMoment] = React.useState(nowMoment);
  const [endMoment, setEndMoment] = React.useState(nowMoment);
  const timezoneGuess = momentTimezone.tz.guess(true);
  const [timezone, setTimezone] = React.useState(timezoneGuess);

  const [scheduledHoursByUserId, setScheduledHoursByUserId] = React.useState(
    {}
  );
  const [loadingScheduledHours, setLoadingScheduledHours] = React.useState(
    false
  );

  const [updatePending, setUpdatePending] = React.useState(false);
  const [deletePending, setDeletePending] = React.useState(false);
  const [publishPending, setPublishPending] = React.useState(false);
  const [reminderPending, setReminderPending] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [coverageApprovedLoading, setCoverageApprovedLoading] = React.useState(
    false
  );
  const [coverageDeniedLoading, setCoverageDeniedLoading] = React.useState(
    false
  );

  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const calendarBounds = useCalendarBounds(
    startMoment.format(constants.calendar.DATE_FORMAT)
  );

  const [alertDialog, setAlertDialog] = React.useState({
    open: false,
    title: "",
    message: "",
    confirmText: "",
    onConfirmPress: () => null,
    cancelText: "",
    onCancelPress: () => null,
  });
  const [propertiesSearchDialog, setPropertiesSearchDialog] = React.useState({
    open: false,
  });
  const [usersSearchDialog, setUsersSearchDialog] = React.useState({
    open: false,
  });

  const postOrders = useSelector((state) => state.postOrders.postOrders);
  const appUserId = useSelector((state) => state.auth.user.username);
  const serviceProviderId = useSelector(
    (state) => state.serviceProvider.serviceProvider.serviceProviderId
  );
  const propertiesById = useSelector((state) => state.properties.byId);
  const usersById = useSelector((state) => state.users.byId);
  const divisionUsersById = useSelector((state) =>
    shift !== undefined
      ? getAllDivisionUsersById({
          division: state.divisions.byId[shift.divisionId],
        })
      : {}
  );
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (shift !== undefined && shift !== null) {
      const getUserShifts = () => {
        setLoadingScheduledHours(true);
        appsyncOperations
          .queryShifts({
            serviceProviderId,
            from: calendarBounds.week.startTime,
            to: calendarBounds.week.endTime,
          })
          .then(
            (shifts) => {
              setScheduledHoursByUserId(
                shifts
                  .filter((filterShift) => {
                    return (
                      filterShift.shiftId !== shift.shiftId &&
                      filterShift.userId !== undefined &&
                      filterShift.userId !== null &&
                      filterShift.status !==
                        constants.shift.status.timeOffRequested.value &&
                      filterShift.status !==
                        constants.shift.status.timeOffApproved.value
                    );
                  })
                  .reduce((accumulator, shift) => {
                    const nextScheduledHoursByUserId = { ...accumulator };
                    const hours =
                      (shift.endTime - shift.startTime) /
                      3600000; /* one hour */
                    nextScheduledHoursByUserId[shift.userId] =
                      nextScheduledHoursByUserId[shift.userId] !== undefined
                        ? nextScheduledHoursByUserId[shift.userId] + hours
                        : hours;
                    return nextScheduledHoursByUserId;
                  }, {})
              );
              setLoadingScheduledHours(false);
            },
            (error) => {
              enqueueSnackbar(error, { variant: "error" });
              setLoadingScheduledHours(false);
            }
          );
      };
      getUserShifts();
    } else {
      setScheduledHoursByUserId({});
    }
  }, [calendarBounds.week.startTime, calendarBounds.week.endTime]);
  React.useEffect(() => {
    if (open && Boolean(shift)) {
      dispatch(init({ postOrders: shift.postOrders ? shift.postOrders : [] }));
      setEditing(false);
    } else {
      dispatch(init({ postOrders: [] }));
    }
  }, [open, shift]);
  React.useEffect(() => {
    if (editing && Boolean(shift)) {
      setPropertyId(shift.propertyId);
      setUserId(shift.userId);
      setStartMoment(moment(shift.startTime));
      setEndMoment(moment(shift.endTime));
      setTimezone(shift.timezone ? shift.timezone : timezoneGuess);
    }
  }, [editing, shift]);

  const classes = useStyles();

  if (shift !== undefined && shift !== null) {
    const enableEditing = () => setEditing(true);
    const openPropertiesSearch = () => {
      if (editing) {
        setPropertiesSearchDialog({ open: true });
      }
    };
    const closePropertiesSearch = () =>
      setPropertiesSearchDialog({ open: false });
    const openUsersSearch = () => {
      if (editing) {
        setUsersSearchDialog({ open: true });
      }
    };
    const closeUsersSearch = () => setUsersSearchDialog({ open: false });
    const updateShift = () => {
      if (startMoment.isAfter(endMoment)) {
        enqueueSnackbar("Start time cannot be after the end time.", {
          variant: "error",
        });
      } else {
        setUpdatePending(true);
        appsyncOperations
          .updateShift({
            shiftId: shift.shiftId,
            updatedBy: appUserId,
            startTime: startMoment.valueOf(),
            endTime: endMoment.valueOf(),
            propertyId,
            userId,
            serviceProviderId: serviceProviderId,
            timezone: shift.timezone,
            postOrders,
          })
          .then(
            (updatedShift) => {
              setUpdatePending(false);
              setEditing(false);
              onSave(updatedShift);
              enqueueSnackbar("Update successful!", { variant: "success" });
            },
            (error) => {
              setUpdatePending(false);
              enqueueSnackbar(error, { variant: "error" });
            }
          );
      }
    };
    const deleteShift = () => {
      setAlertDialog({
        open: true,
        title: "Delete shift?",
        message: "Are you sure you want to delete this shift?",
        confirmText: "Confirm",
        onConfirmPress: () => {
          setAlertDialog((prevState) => ({ ...prevState, open: false }));
          setDeletePending(true);
          appsyncOperations
            .deleteShift({
              shiftId: shift.shiftId,
              updatedBy: appUserId,
              serviceProviderId,
            })
            .then(
              (deletedShift) => {
                setDeletePending(false);
                onDelete(deletedShift);
                onClose();
                enqueueSnackbar("Delete successful!", { variant: "success" });
              },
              (error) => {
                setDeletePending(false);
                enqueueSnackbar(error, { variant: "error" });
              }
            );
        },
        cancelText: "Cancel",
        onCancelPress: () => {
          setAlertDialog((prevState) => ({ ...prevState, open: false }));
        },
      });
    };
    const publishShift = () => {
      setAlertDialog({
        open: true,
        title: "Publish shift?",
        message: "Are you sure you want to publish this shift?",
        confirmText: "Confirm",
        onConfirmPress: () => {
          setAlertDialog((prevState) => ({ ...prevState, open: false }));
          setPublishPending(true);
          appsyncOperations.publishShifts([shift.shiftId]).then(
            (publishedShifts) => {
              setPublishPending(false);
              onPublish(publishedShifts[0]);
              onClose();
              enqueueSnackbar("Publish successful!", { variant: "success" });
            },
            (error) => {
              setPublishPending(false);
              enqueueSnackbar(error, { variant: "error" });
            }
          );
        },
        cancelText: "Cancel",
        onCancelPress: () => {
          setAlertDialog((prevState) => ({ ...prevState, open: false }));
        },
      });
    };
    const sendReminder = () => {
      setReminderPending(true);
      appsyncOperations.shiftReminders([shift.shiftId]).then(
        (shifts) => {
          setReminderPending(false);
          enqueueSnackbar("Reminder sent!", { variant: "success" });
        },
        (error) => {
          setReminderPending(false);
          enqueueSnackbar(error, { variant: "error" });
        }
      );
    };
    const cancelUpdate = () => {
      dispatch(init({ postOrders: shift.postOrders ? shift.postOrders : [] }));
      setEditing(false);
    };

    const property = propertiesById[!editing ? shift.propertyId : propertyId];
    const user = usersById[!editing ? shift.userId : userId];
    const shiftPublished =
      shift.status !== constants.shift.status.created.value;
    const shiftStarted = Date.now() >= shift.startTime;
    const shiftEnded = Date.now() > shift.endTime;
    const openShift =
      shift.userId === undefined ||
      shift.userId === null ||
      shift.status === constants.shift.status.coverageApproved.value;

    let duration;
    if (editing) {
      const diff = endMoment.diff(startMoment);
      if (diff > 0) {
        duration = diff / 3600000;
      } else {
        duration = 0;
      }
    } else {
      duration = (shift.endTime - shift.startTime) / 3600000;
    }
    let scheduledHours;
    let potentialHours;
    if (user !== undefined) {
      if (loadingScheduledHours) {
        scheduledHours = "Calculating hours...";
      } else {
        const userScheduledHours =
          scheduledHoursByUserId[user.userId] !== undefined
            ? scheduledHoursByUserId[user.userId]
            : 0;
        if (!editing || (shiftPublished && shiftStarted)) {
          scheduledHours = `${(userScheduledHours + duration).toFixed(2)}`;
        } else {
          scheduledHours = userScheduledHours.toFixed(2);
          potentialHours = `${(userScheduledHours + duration).toFixed(2)}`;
        }
      }
    }
    let levels;
    if (
      openShift &&
      Array.isArray(shift.requiredLevels) &&
      shift.requiredLevels.length > 0
    ) {
      if (shift.requiredLevels.length === 1) {
        levels = `Level ${getUserLevelAvatar(shift.requiredLevels[0])} only`;
      } else if (shift.requiredLevels.length === 2) {
        levels = `Levels ${getUserLevelAvatar(
          shift.requiredLevels[0]
        )} and ${getUserLevelAvatar(shift.requiredLevels[1])}`;
      } else {
        levels = "Levels 1, 2, and 3";
      }
    }

    const handleCoverageApproved = async () => {
      setCoverageApprovedLoading(true);
      try {
        const approvedShift = await appsyncOperations.approveCoverage({
          shiftId: shift.shiftId,
          approvedBy: appUserId,
          levels: [usersById[shift.userId].officerLevel],
        });
        enqueueSnackbar("Shift coverage approved", { variant: "success" });
        onCoverageApproved(approvedShift);
      } catch (e) {
        enqueueSnackbar(e, { variant: "error" });
      }
      setCoverageApprovedLoading(false);
    };

    const handleCoverageDenied = async () => {
      setCoverageDeniedLoading(true);
      try {
        const deniedShift = await appsyncOperations.denyCoverage({
          shiftId: shift.shiftId,
          deniedBy: appUserId,
        });
        onCoverageDenied(deniedShift);
        enqueueSnackbar("Shift coverage denied", { variant: "success" });
      } catch (e) {
        enqueueSnackbar(e, { variant: "error" });
      }
      setCoverageDeniedLoading(false);
    };

    const timeZoneOptions = Object.values(constants.timezones);
    if (shift && shift.timezone) {
      timeZoneOptions.unshift(shift.timezone);
    } else {
      timeZoneOptions.unshift(timezoneGuess);
    }

    return (
      <>
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
          <DialogTitle>
            {!editing
              ? `${
                  openShift ? "Open shift" : "Shift"
                } details \u2022 ${getFormattedShiftStatus(shift.status)}`
              : "Edit shift"}
          </DialogTitle>
          <DialogContent>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {shift.status === "COVERAGE_REQUESTED" && (
                    <Card>
                      <ListItem>
                        <ListItemText
                          primary="User requesting coverage"
                          secondary="After accepting, an admin will need to reassign a user to this shift."
                        />
                      </ListItem>
                      {isUserRole({ admin: true }) && (
                        <CardActions>
                          <LoadingButton
                            isLoading={coverageApprovedLoading}
                            color={"primary"}
                            onClick={handleCoverageApproved}
                          >
                            Approve
                          </LoadingButton>
                          <LoadingButton
                            isLoading={coverageDeniedLoading}
                            color={"primary"}
                            onClick={handleCoverageDenied}
                          >
                            Reject
                          </LoadingButton>
                        </CardActions>
                      )}
                    </Card>
                  )}
                </Grid>
                <Grid item xs={12} />
                {property && (
                  <>
                    <Grid item xs={12}>
                      <Header label="Place" />
                    </Grid>
                    <Grid item xs={12}>
                      <ConditionalTooltip
                        visible={editing && shiftPublished && shiftStarted}
                        title={disabledAfterShiftStart}
                      >
                        <span>
                          <TextField
                            value={
                              property !== undefined
                                ? property.place.formattedAddress
                                : ""
                            }
                            onClick={
                              !(updatePending || (editing && shiftStarted))
                                ? openPropertiesSearch
                                : undefined
                            }
                            disabled={
                              updatePending ||
                              (editing && shiftPublished && shiftStarted)
                            }
                            fullWidth
                            label="Name"
                            InputProps={{
                              endAdornment:
                                !shiftPublished &&
                                editing &&
                                property !== undefined ? (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setPropertyId(null);
                                      }}
                                      edge="end"
                                    >
                                      <ClearIcon />
                                    </IconButton>
                                  </InputAdornment>
                                ) : undefined,
                              readOnly: true,
                            }}
                            variant="outlined"
                          />
                        </span>
                      </ConditionalTooltip>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Header label="Time and date" />
                </Grid>
                <Grid item xs={12}>
                  {!editing ? (
                    <TextField
                      label="Time zone"
                      value={shift.timezone ? shift.timezone : ""}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      fullWidth
                    />
                  ) : (
                    <ConditionalTooltip
                      visible={editing && shiftPublished && shiftStarted}
                      title={disabledAfterShiftStart}
                    >
                      <TextField
                        value={timezone}
                        onChange={(event) => setTimezone(event.target.value)}
                        fullWidth
                        label="Time zone"
                        select
                        SelectProps={{
                          MenuProps: {
                            style: styles.menus,
                          },
                        }}
                        variant="outlined"
                      >
                        {timeZoneOptions.map((timezone) => (
                          <MenuItem key={timezone} value={timezone}>
                            {timezone}
                          </MenuItem>
                        ))}
                      </TextField>
                    </ConditionalTooltip>
                  )}
                </Grid>
                <Grid item xs={9}>
                  {!editing ? (
                    <TextField
                      label="Start date"
                      value={moment(shift.startTime).format("ddd, MMM D, YYYY")}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      fullWidth
                    />
                  ) : (
                    <ConditionalTooltip
                      visible={editing && shiftPublished && shiftStarted}
                      title={disabledAfterShiftStart}
                    >
                      <span>
                        <DatePicker
                          value={startMoment}
                          onChange={(moment) =>
                            setStartMoment(moment.seconds(0))
                          }
                          disabled={
                            updatePending ||
                            (editing && shiftPublished && shiftStarted)
                          }
                          disablePast
                          format="ddd, MMM D, YYYY"
                          fullWidth
                          inputVariant="outlined"
                          label="Start date"
                        />
                      </span>
                    </ConditionalTooltip>
                  )}
                </Grid>
                <Grid item xs={3}>
                  {!editing ? (
                    <TextField
                      label="Start time"
                      value={moment(shift.startTime).format("HH:mm")}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      fullWidth
                    />
                  ) : (
                    <ConditionalTooltip
                      visible={editing && shiftPublished && shiftStarted}
                      title={disabledAfterShiftStart}
                    >
                      <span>
                        <TimePicker
                          value={startMoment}
                          onChange={(moment) =>
                            setStartMoment(moment.seconds(0))
                          }
                          ampm={false}
                          disabled={
                            updatePending ||
                            (editing && shiftPublished && shiftStarted)
                          }
                          format="HH:mm"
                          fullWidth
                          inputVariant="outlined"
                          label="Start time"
                        />
                      </span>
                    </ConditionalTooltip>
                  )}
                </Grid>
                <Grid item xs={9}>
                  {!editing ? (
                    <TextField
                      label="End date"
                      value={moment(shift.endTime).format("ddd, MMM D, YYYY")}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      fullWidth
                    />
                  ) : (
                    <ConditionalTooltip
                      visible={editing && shiftPublished && shiftStarted}
                      title={disabledAfterShiftStart}
                    >
                      <span>
                        <DatePicker
                          value={endMoment}
                          onChange={(moment) => setEndMoment(moment.seconds(0))}
                          disabled={
                            updatePending ||
                            (editing && shiftPublished && shiftStarted)
                          }
                          disablePast
                          format="ddd, MMM D, YYYY"
                          fullWidth
                          inputVariant="outlined"
                          label="End date"
                        />
                      </span>
                    </ConditionalTooltip>
                  )}
                </Grid>
                <Grid item xs={3}>
                  {!editing ? (
                    <TextField
                      value={moment(shift.endTime).format("HH:mm")}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      label="End time"
                      variant="outlined"
                    />
                  ) : (
                    <ConditionalTooltip
                      visible={editing && shiftPublished && shiftStarted}
                      title={disabledAfterShiftStart}
                    >
                      <span>
                        <TimePicker
                          value={endMoment}
                          onChange={(moment) => setEndMoment(moment.seconds(0))}
                          ampm={false}
                          disabled={
                            updatePending ||
                            (editing && shiftPublished && shiftStarted)
                          }
                          format="HH:mm"
                          fullWidth
                          inputVariant="outlined"
                          label="End time"
                        />
                      </span>
                    </ConditionalTooltip>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    value={`${duration.toFixed(2)} hrs`}
                    disabled={editing}
                    fullWidth
                    label="Duration"
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Header label="Assignee" />
                </Grid>
                <Grid item xs={9}>
                  <ConditionalTooltip
                    visible={editing && shiftPublished && !openShift}
                    title={disabledAfterPublish}
                  >
                    <span>
                      <TextField
                        value={user !== undefined ? user.displayName : ""}
                        onClick={
                          !(
                            updatePending ||
                            (editing && shiftPublished && !openShift)
                          )
                            ? openUsersSearch
                            : undefined
                        }
                        disabled={
                          updatePending ||
                          (editing && shiftPublished && !openShift)
                        }
                        fullWidth
                        label="Name"
                        InputProps={{
                          endAdornment:
                            editing &&
                            (!shiftPublished || openShift) &&
                            user !== undefined ? (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setUserId(null);
                                  }}
                                  edge="end"
                                >
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ) : undefined,
                          readOnly: true,
                        }}
                        variant="outlined"
                      />
                    </span>
                  </ConditionalTooltip>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    value={
                      user !== undefined
                        ? getUserLevelAvatar(user.officerLevel)
                        : ""
                    }
                    disabled={editing}
                    fullWidth
                    label="Officer level"
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  xs={!editing || (shiftPublished && shiftStarted) ? 12 : 6}
                >
                  <TextField
                    value={scheduledHours ? scheduledHours : ""}
                    disabled={editing}
                    fullWidth
                    label={`Scheduled hours (${moment(
                      calendarBounds.week.startTime
                    ).format("MMM D")} - ${moment(
                      calendarBounds.week.endTime
                    ).format("MMM D")})`}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                {editing && (!shiftPublished || !shiftStarted) && (
                  <Grid item xs={6}>
                    <TextField
                      value={potentialHours ? potentialHours : ""}
                      disabled={editing}
                      fullWidth
                      label="Hours including this shift"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                    />
                  </Grid>
                )}
                {openShift && (
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        className={classes.labels}
                        color="textSecondary"
                        gutterBottom
                        variant="caption"
                      >
                        {Array.isArray(shift.requests) &&
                        shift.requests.length > 0
                          ? `Applicants (${levels})`
                          : `No applicants yet (${levels}).`}
                      </Typography>
                      {Array.isArray(shift.requests) &&
                        shift.requests.length > 0 && (
                          <Box
                            className={classes.applicants}
                            display="flex"
                            flexDirection="row"
                          >
                            {shift.requests.map((userId) => (
                              <Chip
                                key={userId}
                                label={usersById[userId].displayName}
                              />
                            ))}
                          </Box>
                        )}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <PostOrders
                    isEditable={editing}
                    timezone={shift.timezone ? shift.timezone : timezoneGuess}
                  />
                </Grid>
                {!editing && (
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        className={classes.labels}
                        variant="caption"
                        color="textSecondary"
                        gutterBottom
                      >
                        {`Created by: ${
                          usersById[shift.createdBy] !== undefined
                            ? usersById[shift.createdBy].displayName
                            : "unknown"
                        } \u2022 ${moment(shift.createdAt).format(
                          constants.moment.format
                        )}`}
                      </Typography>
                      <Typography
                        className={classes.labels}
                        variant="caption"
                        color="textSecondary"
                        gutterBottom
                      >
                        {shift.updatedBy
                          ? `
                          Last updated by: ${
                            usersById[shift.updatedBy] !== undefined
                              ? usersById[shift.updatedBy].displayName
                              : "unknown"
                          } \u2022 ${moment(shift.updatedAt).format(
                              constants.moment.format
                            )}
                        `
                          : ""}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </MuiPickersUtilsProvider>
          </DialogContent>
          <DialogActions>
            {!editing ? (
              <>
                {isUserRole({ admin: true }) && (
                  <>
                    <LoadingButton
                      color="primary"
                      onClick={
                        !(deletePending || publishPending || reminderPending)
                          ? deleteShift
                          : undefined
                      }
                      isLoading={deletePending}
                      disabled={
                        deletePending || publishPending || reminderPending
                      }
                    >
                      Delete
                    </LoadingButton>
                    <ConditionalTooltip
                      visible={shiftPublished && shiftEnded}
                      title="Unavailable once shift ends."
                    >
                      <span>
                        <Button
                          color="primary"
                          onClick={
                            !(
                              deletePending ||
                              publishPending ||
                              reminderPending ||
                              (shiftPublished && shiftEnded)
                            )
                              ? enableEditing
                              : undefined
                          }
                          disabled={
                            deletePending ||
                            publishPending ||
                            reminderPending ||
                            (shiftPublished && shiftEnded)
                          }
                        >
                          Edit
                        </Button>
                      </span>
                    </ConditionalTooltip>
                    <ConditionalTooltip
                      visible={shiftPublished}
                      title="Already published."
                    >
                      <span>
                        <LoadingButton
                          color="primary"
                          onClick={
                            !(
                              deletePending ||
                              publishPending ||
                              reminderPending ||
                              shiftPublished
                            )
                              ? publishShift
                              : undefined
                          }
                          isLoading={publishPending}
                          disabled={
                            deletePending ||
                            publishPending ||
                            reminderPending ||
                            shiftPublished
                          }
                        >
                          Publish
                        </LoadingButton>
                      </span>
                    </ConditionalTooltip>
                    <ConditionalTooltip
                      visible={
                        shift.status === constants.shift.status.created.value ||
                        shift.status ===
                          constants.shift.status.acknowledged.value ||
                        (shift.status ===
                          constants.shift.status.published.value &&
                          openShift)
                      }
                      title={getSendReminderDisabledReason(shift.status)}
                    >
                      <span>
                        <LoadingButton
                          color="primary"
                          onClick={
                            !(
                              deletePending ||
                              publishPending ||
                              reminderPending ||
                              shift.status ===
                                constants.shift.status.created.value ||
                              shift.status ===
                                constants.shift.status.acknowledged.value ||
                              (shift.status ===
                                constants.shift.status.published.value &&
                                openShift)
                            )
                              ? sendReminder
                              : undefined
                          }
                          isLoading={reminderPending}
                          disabled={
                            deletePending ||
                            publishPending ||
                            reminderPending ||
                            shift.status ===
                              constants.shift.status.created.value ||
                            shift.status ===
                              constants.shift.status.acknowledged.value ||
                            (shift.status ===
                              constants.shift.status.published.value &&
                              openShift)
                          }
                        >
                          Send Reminder
                        </LoadingButton>
                      </span>
                    </ConditionalTooltip>
                  </>
                )}
                <Button color="primary" onClick={onClose}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button color="primary" onClick={cancelUpdate}>
                  Cancel
                </Button>
                <LoadingButton
                  color="primary"
                  onClick={!updatePending ? updateShift : undefined}
                  isLoading={updatePending}
                  disabled={updatePending}
                >
                  Save
                </LoadingButton>
              </>
            )}
          </DialogActions>
        </Dialog>
        {open && (
          <>
            <Dialog
              open={alertDialog.open}
              onClose={() => setAlertDialog({ open: false })}
            >
              <DialogTitle>{alertDialog.title}</DialogTitle>
              <DialogContent>
                <DialogContentText>{alertDialog.message}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button color="primary" onClick={alertDialog.onCancelPress}>
                  {alertDialog.cancelText}
                </Button>
                <Button color="primary" onClick={alertDialog.onConfirmPress}>
                  {alertDialog.confirmText}
                </Button>
              </DialogActions>
            </Dialog>
            {editing && (
              <>
                <PropertiesSearchDialog
                  open={propertiesSearchDialog.open}
                  onClose={closePropertiesSearch}
                  onPropertyClick={(propertyId) => setPropertyId(propertyId)}
                  divisionId={shift.divisionId}
                />
                <UsersSearchDialog
                  open={usersSearchDialog.open}
                  onClose={closeUsersSearch}
                  onUserClick={(userId) => {
                    setUserId(userId);
                    closeUsersSearch();
                  }}
                  users={Object.values(divisionUsersById).map((user) => {
                    const scheduledHours = scheduledHoursByUserId[user.userId];
                    const shiftApplicant =
                      Array.isArray(shift.requests) &&
                      shift.requests.includes(user.userId);
                    return {
                      ...user,
                      scheduledHours,
                      shiftApplicant,
                    };
                  })}
                  week={`${moment(calendarBounds.week.startTime).format(
                    "MMM D"
                  )} - ${moment(calendarBounds.week.endTime).format("MMM D")}`}
                />
              </>
            )}
          </>
        )}
      </>
    );
  } else {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Sorry.</DialogTitle>
        <DialogContent>
          <DialogContentText>
            We couldn't find what you were looking for.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
};

const disabledAfterPublish = "Cannot be set after a shift has been published.";
const disabledAfterShiftStart =
  "Cannot be set after a published shift has started.";
const getSendReminderDisabledReason = (shiftStatus) => {
  if (shiftStatus === constants.shift.status.created.value) {
    return "Must first be published.";
  } else if (shiftStatus === constants.shift.status.acknowledged.value) {
    return "Shift has been acknowledged.";
  } else {
    return "Must be assigned a person first.";
  }
};

const styles = {
  menu: {
    width: 200,
  },
};

const useStyles = makeStyles((theme) => ({
  applicants: {
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
  labels: {
    paddingLeft: 14,
    paddingRight: 14,
  },
}));

export default ShiftDetailsDialog;
